interface GraphQLClientOptions {
  endpoint: string;
  retries?: number;
  retryDelayMs?: number;
  headers?: Record<string, string>;
  logRequests?: boolean;
  enableCache?: boolean;
  cacheTTL?: number;
}

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

interface Result<T> {
  data: T;
  error: string;
  success: boolean;
}

export class MinimalGraphQLClient {
  private endpoint: string;
  private retries: number;
  private retryDelayMs: number;
  private headers: Record<string, string>;
  private logRequests: boolean;
  private enableCache: boolean;
  private cacheTTL: number;
  private cache: Map<string, CacheEntry<any>>;

  constructor(options: GraphQLClientOptions) {
    this.endpoint = options.endpoint;
    this.retries = options.retries ?? 2;
    this.retryDelayMs = options.retryDelayMs ?? 250;
    this.headers = options.headers ?? {};
    this.logRequests = options.logRequests ?? false;
    this.enableCache = options.enableCache ?? false;
    this.cacheTTL = options.cacheTTL ?? 60_000;
    this.cache = new Map();
  }

  public async query<T = any, V = Record<string, any>>(query: string, variables?: V): Promise<Result<T>> {
    const cacheKey = this.generateCacheKey(query, variables);

    if (this.enableCache) {
      const cached = this.getFromCache<T>(cacheKey);
      if (cached) return { data: cached as T, success: true, error: null } as Result<T>;
    }

    return this.tryRequest<T, V>(query, variables, cacheKey, 1);
  }

  private async tryRequest<T = any, V = Record<string, any>>(
    query: string,
    variables: V | undefined,
    cacheKey: string,
    attempt: number
  ): Promise<Result<T>> {
    if (this.logRequests) {
      console.log(`[GraphQL] Attempt ${attempt}:`, {
        endpoint: this.endpoint,
        query,
        variables,
      });
    }

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.headers,
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();

      if (result.errors) {
        return { data: null as T, success: false, error: result.errors[0] } as Result<T>;
      }

      const resultObject: Result<T> = { data: result.data as T, success: true, error: null };

      if (this.enableCache) {
        this.cache.set(cacheKey, {
          data: resultObject.data,
          expiresAt: Date.now() + this.cacheTTL,
        });
      }

      return resultObject;
    } catch (err) {
      if (attempt <= this.retries) {
        const delay = this.retryDelayMs * Math.pow(2, attempt - 1);
        if (this.logRequests) {
          console.warn(`[GraphQL] Retry ${attempt} failed, retrying in ${delay}ms...`);
        }
        await new Promise((res) => setTimeout(res, delay));
        return this.tryRequest(query, variables, cacheKey, attempt + 1);
      }
      throw err;
    }
  }

  public clearCache(): void {
    this.cache.clear();
  }

  private generateCacheKey(query: string, variables?: any): string {
    return JSON.stringify({ query, variables });
  }

  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now < entry.expiresAt) {
      if (this.logRequests) {
        console.log('[GraphQL] Cache hit');
      }
      return entry.data as T;
    }

    if (this.logRequests) {
      console.log('[GraphQL] Cache expired');
    }
    this.cache.delete(key);
    return null;
  }
}
